import 'dayjs/locale/en-gb';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/zh-tw';
import 'dayjs/locale/th';
import 'dayjs/locale/vi';
import 'dayjs/locale/ms-my';
import 'dayjs/locale/id';
import 'dayjs/locale/ja';
import 'dayjs/locale/ko';
import 'dayjs/locale/ar';
import 'dayjs/locale/es';
import 'dayjs/locale/pt-br';

import dayjs, { Dayjs } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import localeData from 'dayjs/plugin/localeData';
import minMax from 'dayjs/plugin/minMax';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekday from 'dayjs/plugin/weekday';

import { EDayjsUnit, IGetStartOrEndOfPeriodParams, IGetUTCOffsetDateTimeAsDayjsParams, TDayJsInput } from './types';

export * from './types';

// strict thresholds
const thresholds = [
  { l: 's', r: 59, d: 'second' },
  { l: 'm', r: 1 },
  { l: 'mm', r: 59, d: 'minute' },
  { l: 'h', r: 1 },
  { l: 'hh', r: 23, d: 'hour' },
  { l: 'd', r: 1 },
  { l: 'dd', r: 29, d: 'day' },
  { l: 'M', r: 1 },
  { l: 'MM', r: 11, d: 'month' },
  { l: 'y', r: 1 },
  { l: 'yy', d: 'year' }
];

dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(isBetween);
dayjs.extend(localeData);
dayjs.extend(minMax);
dayjs.extend(relativeTime, { thresholds, rounding: Math.floor });
dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(weekday);
dayjs.extend(weekOfYear);
dayjs.extend(duration);

dayjs.Ls.en.weekStart = 1;

export const DATETIME_SECONDS_FORMAT = 'YYYY-MM-DD HH:mm:ss';
export const DATETIME_NOTIFICATION_FORMAT = 'DD/MM/YYYY HH:mm:ss';
export const DATETIME_FORMAT = 'YYYY-MM-DD HH:mm';
export const DATE_FORMAT = 'YYYY-MM-DD';
export const DATE_MONTH_YEAR_HYPHEN_FORMAT = 'DD-MM-YYYY';
export const YEAR_MONTH_HYPHEN_FORMAT = 'YYYY-MM';
export const DATE_MONTH_YEAR_FORMAT = 'DDMMYYYY';
export const DATE_MONTH_TEXT_YEAR_FORMAT = 'DD MMM YYYY';
export const LONG_MONTH_YEAR_FORMAT = 'MMMM YYYY';

export default dayjs;

export const getDayjsTZ = () => dayjs.tz.guess() ?? 'Asia/Singapore';
let dayjsTZ = getDayjsTZ();

export const setUserTimeZone = (tz: string) => {
  dayjsTZ = tz;
};

export const getUserTimeZone = () => dayjsTZ;

/**
 * To CONVERT input into timezone aware dayjs object.
 *
 * @param date: Input to convert
 * @param tz: Timezone to convert input to
 */
export const toDayjs = (date?: TDayJsInput | null, tz: string = dayjsTZ) =>
  dayjs(date === null ? undefined : date).tz(tz);

/**
 * To CONVERT input into timezone aware dayjs object (without changing the time with reference to the timezone, a.k.a. just adding the Timezone value to the object without correcting the time to the timezone)
 *
 * @param date: Input to convert
 * @param tz: Timezone to convert input to
 */
export const toDayjsKeepTime = (date?: TDayJsInput, tz = dayjsTZ) =>
  dayjs(date === null ? undefined : date).tz(tz, true);

/**
 * To PARSE input into timezone aware dayjs object, in the specified timezone
 *
 * @param date: Input to convert
 * @param tz: Timezone to convert input to
 */
export const parseDayjs = (date?: TDayJsInput, tz = dayjsTZ) => dayjs.tz(date === null ? undefined : date, tz);

export const setDayjsTZ = (date: Dayjs, tz = dayjsTZ) => date.tz(tz);

export const setDayjsTZKeepTime = (date: Dayjs, tz = dayjsTZ) => date.tz(tz, true);

export const formatDayjs = (date?: TDayJsInput, formatStr = DATETIME_SECONDS_FORMAT, tz = dayjsTZ) =>
  toDayjs(date, tz).format(formatStr);

export const getSecondsSinceEpoch = (date?: Dayjs, excludeMs?: boolean) =>
  excludeMs ? date?.unix() : date?.millisecond(0).valueOf();

export const getStartOrEndOfPeriod = ({
  date,
  isStartOf = true,
  unit = EDayjsUnit.Day,
  tz = dayjsTZ
}: IGetStartOrEndOfPeriodParams) => (isStartOf ? toDayjs(date, tz).startOf(unit) : toDayjs(date, tz).endOf(unit));

export const getStartOrEndOfPeriodInSeconds = ({ date, ...restParams }: IGetStartOrEndOfPeriodParams) =>
  date ? getSecondsSinceEpoch(getStartOrEndOfPeriod({ date, ...restParams })) : undefined;

export const getCurrentMonthDateRange = (disableFutureDates?: boolean): [Date, Date] => {
  const today = dayjs();

  return [
    today.startOf('month').toDate(),
    disableFutureDates
      ? getMinimumDate(today.endOf('day'), today.endOf('month')).toDate()
      : today.endOf('month').toDate()
  ];
};

export const getLastMonthDateRange = (): [Date, Date] => {
  const lastMonth = dayjs().subtract(1, 'month');

  return [lastMonth.startOf('month').toDate(), lastMonth.endOf('month').toDate()];
};

export const getPast6MonthsDateRange = (): [Date, Date] => {
  const lastMonth = dayjs().subtract(1, 'month');
  const last6Month = dayjs().subtract(6, 'months');

  return [last6Month.startOf('month').toDate(), lastMonth.endOf('month').toDate()];
};

export const getCurrentYearDateRange = (disableFutureDates?: boolean): [Date, Date] => {
  const today = dayjs();

  return [
    today.startOf('year').toDate(),
    disableFutureDates ? getMinimumDate(today.endOf('day'), today.endOf('year')).toDate() : today.endOf('year').toDate()
  ];
};

export const getLastYearDateRange = (): [Date, Date] => {
  const lastYear = dayjs().subtract(1, 'year');

  return [lastYear.startOf('year').toDate(), lastYear.endOf('year').toDate()];
};

export const getLast90DaysDateRange = (): [Date, Date] => {
  const now = dayjs();
  const todayEnd = now.endOf('day').toDate();

  return [now.subtract(89, 'day').startOf('day').toDate(), todayEnd];
};

export const getLast30DaysDateRange = (): [Date, Date] => {
  const now = dayjs();
  const todayEnd = now.endOf('day').toDate();

  return [now.subtract(29, 'day').startOf('day').toDate(), todayEnd];
};

export const getLast7DaysDateRange = (): [Date, Date] => {
  const now = dayjs();
  const todayEnd = now.endOf('day').toDate();

  return [now.subtract(6, 'day').startOf('day').toDate(), todayEnd];
};

/**
 * To RETURN the earlier date of either date inputs
 *
 * @param dateA: First date in dayjs.Dayjs
 * @param dateB: Second date in dayjs.Dayjs
 *
 * This method can be used in place of dayjs.min(), as dayjs.min() is throwing null if no date or only one date is passed as parameters
 */
export const getMinimumDate = (dateA: dayjs.Dayjs, dateB: dayjs.Dayjs) => {
  const minimumDate = dayjs.min(dateA, dateB);

  if (minimumDate === null) {
    return dateA.isAfter(dateB) ? dateB : dateA;
  } else {
    return minimumDate;
  }
};

export const getUTCOffsetDateTimeAsDayjs = ({
  input,
  utcOffset,
  keepTime,
  baseFromOffset
}: IGetUTCOffsetDateTimeAsDayjsParams = {}) => {
  let dayjsObj = dayjs(input);
  if (utcOffset !== undefined) {
    dayjsObj = dayjsObj.utcOffset(utcOffset, keepTime);
  }
  return baseFromOffset ? dayjs(dayjsObj.format(DATETIME_SECONDS_FORMAT)) : dayjsObj;
};
